import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, Clock, Camera, FileText, Send,
  Megaphone, Plus, Filter, LayoutGrid, List, CalendarDays, X, Edit,
  Trash2, ExternalLink, Palette, Image, CheckCircle, Archive, AlertCircle,
  Eye, Link2, CalendarRange, Layers, Tag, MoreHorizontal, Lock
} from 'lucide-react';
import { PermissionButton, PermissionGate, usePermissions, FEATURE_PERMISSIONS } from '../../components/common/PermissionButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event type configuration
const eventTypeConfig = {
  campaign_start: { color: 'bg-orange-500', textColor: 'text-orange-500', label: 'Campaign Start', icon: Megaphone },
  campaign_end: { color: 'bg-orange-300', textColor: 'text-orange-400', label: 'Campaign End', icon: Megaphone },
  content_deadline: { color: 'bg-red-500', textColor: 'text-red-500', label: 'Deadline', icon: Clock },
  publish_scheduled: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Scheduled Publish', icon: Send },
  design_scheduled: { color: 'bg-purple-500', textColor: 'text-purple-500', label: 'Design', icon: Palette },
  agency_deadline: { color: 'bg-purple-500', textColor: 'text-purple-500', label: 'Agency Deadline', icon: Clock },
  meeting: { color: 'bg-green-500', textColor: 'text-green-500', label: 'Meeting', icon: Calendar },
  other: { color: 'bg-slate-500', textColor: 'text-slate-500', label: 'Other', icon: Calendar }
};

// Status configuration
const statusConfig = {
  draft: { color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300', label: 'Draft', icon: Edit },
  scheduled: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', label: 'Scheduled', icon: Clock },
  completed: { color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', label: 'Completed', icon: CheckCircle },
  published: { color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400', label: 'Published', icon: CheckCircle },
  archived: { color: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400', label: 'Archived', icon: Archive },
  cancelled: { color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', label: 'Cancelled', icon: X }
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ==========================================
// Event Detail Side Panel Component
// ==========================================
const EventDetailPanel = ({ event, onClose, onUpdate, onDelete, onOpenDesign, permissions }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    status: event?.status || 'scheduled',
    start: event?.start ? new Date(event.start).toISOString().slice(0, 16) : '',
    channel: event?.channel || ''
  });

  const config = eventTypeConfig[event?.event_type] || eventTypeConfig.other;
  const Icon = config.icon;
  const statusCfg = statusConfig[event?.status] || statusConfig.scheduled;
  const StatusIcon = statusCfg.icon;

  // Permission checks
  const canEdit = permissions?.[FEATURE_PERMISSIONS.SCHEDULER_EDIT];
  const canDelete = permissions?.[FEATURE_PERMISSIONS.SCHEDULER_DELETE];
  const canChangeStatus = permissions?.[FEATURE_PERMISSIONS.SCHEDULER_STATUS];

  const handleSave = async () => {
    await onUpdate(event.id, {
      title: formData.title,
      description: formData.description,
      status: formData.status,
      start: new Date(formData.start).toISOString()
    });
    setEditing(false);
  };

  if (!event) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-slate-800 border-l dark:border-slate-700 shadow-xl z-40 flex flex-col" data-testid="event-detail-panel">
      {/* Header */}
      <div className="p-4 border-b dark:border-slate-700 flex items-center justify-between">
        <h3 className="font-semibold dark:text-white">Event Details</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4">
        {/* Preview Image (if design) */}
        {event.design_id && event.preview_image && (
          <div className="mb-4">
            <img 
              src={event.preview_image} 
              alt="Design preview" 
              className="w-full rounded-lg border dark:border-slate-700"
            />
          </div>
        )}

        {/* Event Type Badge */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`p-2 rounded-lg ${config.color} text-white`}>
            <Icon className="h-4 w-4" />
          </div>
          <Badge variant="outline">{config.label}</Badge>
          <Badge className={statusCfg.color}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusCfg.label}
          </Badge>
        </div>

        {/* Title */}
        {editing ? (
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="dark:bg-slate-700"
                rows={3}
              />
            </div>
            <div>
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={formData.start}
                onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">Save</Button>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold dark:text-white">{event.title}</h2>
              {event.description && (
                <p className="text-muted-foreground dark:text-slate-400 mt-2">{event.description}</p>
              )}
            </div>

            <Separator />

            {/* Metadata */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="dark:text-slate-300">
                  {new Date(event.start).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="dark:text-slate-300">
                  {new Date(event.start).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {event.channel && (
                <div className="flex items-center gap-2 text-sm">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="dark:text-slate-300 capitalize">{event.channel}</span>
                </div>
              )}
              {event.design_id && (
                <div className="flex items-center gap-2 text-sm">
                  <Palette className="h-4 w-4 text-purple-500" />
                  <span className="dark:text-slate-300">Created from Content Studio</span>
                </div>
              )}
            </div>

            <Separator />

            {/* Actions */}
            <div className="space-y-2">
              {canEdit ? (
                <Button variant="outline" className="w-full justify-start" onClick={() => setEditing(true)}>
                  <Edit className="h-4 w-4 mr-2" /> Edit Event
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-full">
                        <Button variant="outline" className="w-full justify-start opacity-50 cursor-not-allowed" disabled>
                          <Lock className="h-4 w-4 mr-2" /> Edit Event
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>You do not have permission to edit events</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {event.design_id && (
                <Button variant="outline" className="w-full justify-start" onClick={() => onOpenDesign(event.design_id)}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Open Design
                </Button>
              )}
              {canDelete ? (
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => onDelete(event.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete Event
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-full">
                        <Button variant="outline" className="w-full justify-start opacity-50 cursor-not-allowed" disabled>
                          <Lock className="h-4 w-4 mr-2" /> Delete Event
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>You do not have permission to delete events</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// ==========================================
// Create/Edit Event Dialog
// ==========================================
const EventDialog = ({ open, onClose, onSubmit, event = null, designData = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'publish_scheduled',
    start: new Date().toISOString().slice(0, 16),
    channel: '',
    status: 'scheduled',
    design_id: null,
    preview_image: null
  });

  // Initialize form data based on props
  const initRef = React.useRef({ event: null, designData: null, open: false });
  
  useEffect(() => {
    // Only update if props changed
    if (
      initRef.current.event === event &&
      initRef.current.designData === designData &&
      initRef.current.open === open
    ) {
      return;
    }
    initRef.current = { event, designData, open };
    
    // Use queueMicrotask to avoid cascading renders warning
    queueMicrotask(() => {
      if (designData) {
        setFormData({
          title: designData.name || 'Scheduled Design',
          description: `Design from Content Studio`,
          event_type: 'design_scheduled',
          start: new Date().toISOString().slice(0, 16),
          channel: '',
          status: 'scheduled',
          design_id: designData.design_id,
          preview_image: designData.preview_image
        });
      } else if (event) {
        setFormData({
          title: event.title,
          description: event.description || '',
          event_type: event.event_type,
          start: new Date(event.start).toISOString().slice(0, 16),
          channel: event.channel || '',
          status: event.status || 'scheduled',
          design_id: event.design_id || null,
          preview_image: event.preview_image || null
        });
      } else {
        setFormData({
          title: '',
          description: '',
          event_type: 'publish_scheduled',
          start: new Date().toISOString().slice(0, 16),
          channel: '',
          status: 'scheduled',
          design_id: null,
          preview_image: null
        });
      }
    });
  }, [event, designData, open]);

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    onSubmit({
      ...formData,
      start: new Date(formData.start).toISOString()
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="dark:bg-slate-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <CalendarDays className="h-5 w-5 text-purple-500" />
            {event ? 'Edit Event' : 'Schedule Content'}
          </DialogTitle>
          <DialogDescription>
            {designData ? 'Schedule your design from Content Studio' : 'Create a new scheduled event'}
          </DialogDescription>
        </DialogHeader>

        {/* Design Preview */}
        {formData.preview_image && (
          <div className="mb-4">
            <img 
              src={formData.preview_image} 
              alt="Design preview" 
              className="w-full h-32 object-cover rounded-lg border dark:border-slate-700"
            />
          </div>
        )}

        <div className="space-y-4 py-4">
          <div>
            <Label className="dark:text-slate-200">Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Event title"
              className="dark:bg-slate-700"
            />
          </div>

          <div>
            <Label className="dark:text-slate-200">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
              className="dark:bg-slate-700"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="dark:text-slate-200">Event Type</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="publish_scheduled">Scheduled Publish</SelectItem>
                  <SelectItem value="design_scheduled">Design</SelectItem>
                  <SelectItem value="content_deadline">Content Deadline</SelectItem>
                  <SelectItem value="campaign_start">Campaign Start</SelectItem>
                  <SelectItem value="campaign_end">Campaign End</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="dark:text-slate-200">Channel</Label>
              <Select value={formData.channel || 'none'} onValueChange={(v) => setFormData({ ...formData, channel: v === 'none' ? '' : v })}>
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="blog">Blog</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="dark:text-slate-200">Date & Time</Label>
              <Input
                type="datetime-local"
                value={formData.start}
                onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                className="dark:bg-slate-700"
              />
            </div>

            <div>
              <Label className="dark:text-slate-200">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="schedule-submit">
            {event ? 'Update Event' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// Main Scheduler Component
// ==========================================
export const Scheduler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Phase 3.5: Permission checks
  const permissions = usePermissions([
    FEATURE_PERMISSIONS.SCHEDULER_CREATE,
    FEATURE_PERMISSIONS.SCHEDULER_EDIT,
    FEATURE_PERMISSIONS.SCHEDULER_DELETE,
    FEATURE_PERMISSIONS.SCHEDULER_STATUS
  ]);

  const canCreate = permissions[FEATURE_PERMISSIONS.SCHEDULER_CREATE];
  const canEdit = permissions[FEATURE_PERMISSIONS.SCHEDULER_EDIT];
  const canDelete = permissions[FEATURE_PERMISSIONS.SCHEDULER_DELETE];
  const canChangeStatus = permissions[FEATURE_PERMISSIONS.SCHEDULER_STATUS];

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // month, week, list
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Filters
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // none, status, type, channel
  
  // UI State
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [pendingDesignData, setPendingDesignData] = useState(null);

  // Check for scheduled content from Content Studio
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'schedule') {
      const storedContent = sessionStorage.getItem('scheduled_content');
      if (storedContent) {
        try {
          const designData = JSON.parse(storedContent);
          setPendingDesignData(designData);
          setShowEventDialog(true);
          sessionStorage.removeItem('scheduled_content');
        } catch (e) {
          console.error('Failed to parse scheduled content:', e);
        }
      }
    }
  }, [searchParams]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Get date range based on view
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      let start, end;
      
      if (viewMode === 'week') {
        const dayOfWeek = currentDate.getDay();
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - dayOfWeek);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        start = weekStart.toISOString();
        end = weekEnd.toISOString();
      } else {
        start = new Date(year, month, 1).toISOString();
        end = new Date(year, month + 2, 0).toISOString();
      }
      
      const res = await axios.get(`${API}/marketing/calendar/events?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Navigation
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // CRUD Operations
  const createEvent = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/calendar/events`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event scheduled!');
      setShowEventDialog(false);
      setPendingDesignData(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
      console.error(error);
    }
  };

  const updateEvent = async (id, data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.patch(`${API}/marketing/calendar/events/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event updated!');
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event');
      console.error(error);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/calendar/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openDesign = (designId) => {
    navigate(`/marketing-hub/content-studio?tab=designs&edit=${designId}`);
  };

  // Calendar generation
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    
    const days = [];
    
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, daysInPrevMonth - i)
      });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: i,
        isCurrentMonth: true,
        fullDate: new Date(year, month, i)
      });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: i,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, i)
      });
    }
    
    return days;
  };

  const generateWeekDays = () => {
    const dayOfWeek = currentDate.getDay();
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - dayOfWeek);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push({
        date: day.getDate(),
        dayName: DAYS_FULL[i],
        isCurrentMonth: day.getMonth() === currentDate.getMonth(),
        fullDate: day
      });
    }
    return days;
  };

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Filtering
  const filteredEvents = events.filter(event => {
    if (filterChannel !== 'all' && event.channel !== filterChannel) return false;
    if (filterStatus !== 'all' && event.status !== filterStatus) return false;
    if (filterType !== 'all' && event.event_type !== filterType) return false;
    return true;
  });

  // Grouping for list view
  const groupedEvents = () => {
    if (groupBy === 'none') return { 'All Events': filteredEvents };
    
    return filteredEvents.reduce((groups, event) => {
      let key;
      switch (groupBy) {
        case 'status':
          key = statusConfig[event.status]?.label || 'Unknown';
          break;
        case 'type':
          key = eventTypeConfig[event.event_type]?.label || 'Other';
          break;
        case 'channel':
          key = event.channel ? event.channel.charAt(0).toUpperCase() + event.channel.slice(1) : 'No Channel';
          break;
        default:
          key = 'All';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
      return groups;
    }, {});
  };

  const calendarDays = generateCalendarDays();
  const weekDays = generateWeekDays();

  // Get week range for header
  const getWeekRange = () => {
    const dayOfWeek = currentDate.getDay();
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - dayOfWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    return `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()} - ${weekStart.getMonth() !== weekEnd.getMonth() ? MONTHS[weekEnd.getMonth()] + ' ' : ''}${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="scheduler-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <CalendarDays className="h-8 w-8 text-purple-500" />
              Marketing Scheduler
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Plan and schedule your marketing content
            </p>
          </div>
          <PermissionButton
            permission={FEATURE_PERMISSIONS.SCHEDULER_CREATE}
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => { setEditingEvent(null); setPendingDesignData(null); setShowEventDialog(true); }}
            data-testid="add-event-btn"
            tooltipText="You don't have permission to create scheduled events"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </PermissionButton>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => viewMode === 'week' ? navigateWeek(-1) : navigateMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold dark:text-white min-w-[220px] text-center">
              {viewMode === 'week' 
                ? getWeekRange()
                : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              }
            </h2>
            <Button variant="outline" size="icon" onClick={() => viewMode === 'week' ? navigateWeek(1) : navigateMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="ml-2">
              Today
            </Button>
          </div>
          
          {/* Filters & View Toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Channel Filter */}
            <Select value={filterChannel} onValueChange={setFilterChannel}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="twitter">Twitter</SelectItem>
                <SelectItem value="blog">Blog</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            {/* Group By (List view only) */}
            {viewMode === 'list' && (
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-[130px]">
                  <Layers className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Grouping</SelectItem>
                  <SelectItem value="status">By Status</SelectItem>
                  <SelectItem value="type">By Type</SelectItem>
                  <SelectItem value="channel">By Channel</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {/* View Mode Toggle */}
            <div className="flex border rounded-lg overflow-hidden dark:border-slate-700">
              <Button
                variant={viewMode === 'month' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('month')}
                className="rounded-none"
                data-testid="view-month"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'week' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('week')}
                className="rounded-none"
                data-testid="view-week"
              >
                <CalendarRange className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
                data-testid="view-list"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Month View */}
            {viewMode === 'month' && (
              <Card className="dark:bg-slate-800/60 dark:border-slate-700 overflow-hidden" data-testid="calendar-month">
                <CardContent className="p-0">
                  {/* Days Header */}
                  <div className="grid grid-cols-7 border-b dark:border-slate-700">
                    {DAYS.map(day => (
                      <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground dark:text-slate-400 border-r last:border-r-0 dark:border-slate-700">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7">
                    {calendarDays.map((day, idx) => {
                      const dayEvents = getEventsForDay(day.fullDate).filter(e => {
                        if (filterChannel !== 'all' && e.channel !== filterChannel) return false;
                        if (filterStatus !== 'all' && e.status !== filterStatus) return false;
                        return true;
                      });
                      return (
                        <div
                          key={idx}
                          className={`min-h-[100px] p-2 border-r border-b last:border-r-0 dark:border-slate-700 ${
                            day.isCurrentMonth ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/50'
                          } ${isToday(day.fullDate) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        >
                          <span className={`text-sm font-medium ${
                            isToday(day.fullDate) 
                              ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' 
                              : day.isCurrentMonth 
                                ? 'dark:text-white' 
                                : 'text-slate-400 dark:text-slate-500'
                          }`}>
                            {day.date}
                          </span>
                          <div className="mt-1 space-y-1">
                            {dayEvents.slice(0, 3).map((event, eventIdx) => {
                              const config = eventTypeConfig[event.event_type] || eventTypeConfig.other;
                              return (
                                <div
                                  key={eventIdx}
                                  className={`text-xs p-1 rounded truncate text-white cursor-pointer hover:opacity-80 ${config.color}`}
                                  title={event.title}
                                  onClick={() => setSelectedEvent(event)}
                                  data-testid={`event-${event.id}`}
                                >
                                  {event.design_id && <Palette className="h-3 w-3 inline mr-1" />}
                                  {event.title}
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-muted-foreground dark:text-slate-400 cursor-pointer hover:text-blue-500">
                                +{dayEvents.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Week View */}
            {viewMode === 'week' && (
              <Card className="dark:bg-slate-800/60 dark:border-slate-700 overflow-hidden" data-testid="calendar-week">
                <CardContent className="p-0">
                  <div className="grid grid-cols-7">
                    {weekDays.map((day, idx) => {
                      const dayEvents = getEventsForDay(day.fullDate).filter(e => {
                        if (filterChannel !== 'all' && e.channel !== filterChannel) return false;
                        if (filterStatus !== 'all' && e.status !== filterStatus) return false;
                        return true;
                      });
                      return (
                        <div
                          key={idx}
                          className={`min-h-[300px] border-r last:border-r-0 dark:border-slate-700 ${
                            isToday(day.fullDate) ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-slate-800'
                          }`}
                        >
                          {/* Day Header */}
                          <div className={`p-3 border-b dark:border-slate-700 text-center ${
                            isToday(day.fullDate) ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-slate-50 dark:bg-slate-800/60'
                          }`}>
                            <div className="text-xs text-muted-foreground dark:text-slate-400">{day.dayName}</div>
                            <div className={`text-lg font-bold ${isToday(day.fullDate) ? 'text-blue-600 dark:text-blue-400' : 'dark:text-white'}`}>
                              {day.date}
                            </div>
                          </div>
                          
                          {/* Events */}
                          <div className="p-2 space-y-2">
                            {dayEvents.map((event, eventIdx) => {
                              const config = eventTypeConfig[event.event_type] || eventTypeConfig.other;
                              const statusCfg = statusConfig[event.status] || statusConfig.scheduled;
                              return (
                                <div
                                  key={eventIdx}
                                  className={`p-2 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                                    event.design_id 
                                      ? 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20'
                                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                                  }`}
                                  onClick={() => setSelectedEvent(event)}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className={`p-1 rounded ${config.color} text-white`}>
                                      {event.design_id ? <Palette className="h-3 w-3" /> : <config.icon className="h-3 w-3" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium dark:text-white truncate">{event.title}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(event.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                      </p>
                                    </div>
                                  </div>
                                  <Badge className={`mt-1 text-[10px] ${statusCfg.color}`}>
                                    {statusCfg.label}
                                  </Badge>
                                </div>
                              );
                            })}
                            {dayEvents.length === 0 && (
                              <div className="text-xs text-center text-muted-foreground py-4">
                                No events
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <Card className="dark:bg-slate-800/60 dark:border-slate-700" data-testid="event-list">
                <CardHeader>
                  <CardTitle className="dark:text-white flex items-center justify-between">
                    <span>Scheduled Events</span>
                    <Badge variant="outline">{filteredEvents.length} events</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredEvents.length > 0 ? (
                    <div className="space-y-6">
                      {Object.entries(groupedEvents()).map(([groupName, groupEvents]) => (
                        <div key={groupName}>
                          {groupBy !== 'none' && (
                            <h3 className="font-semibold text-sm text-muted-foreground dark:text-slate-400 mb-3 flex items-center gap-2">
                              <Layers className="h-4 w-4" />
                              {groupName} ({groupEvents.length})
                            </h3>
                          )}
                          <div className="space-y-2">
                            {groupEvents
                              .sort((a, b) => new Date(a.start) - new Date(b.start))
                              .map((event, idx) => {
                                const config = eventTypeConfig[event.event_type] || eventTypeConfig.other;
                                const statusCfg = statusConfig[event.status] || statusConfig.scheduled;
                                const Icon = config.icon;
                                return (
                                  <div 
                                    key={idx} 
                                    className={`flex items-center gap-4 p-4 rounded-lg border dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${
                                      event.design_id ? 'border-l-4 border-l-purple-500' : ''
                                    }`}
                                    onClick={() => setSelectedEvent(event)}
                                  >
                                    {/* Preview or Icon */}
                                    {event.preview_image ? (
                                      <img 
                                        src={event.preview_image} 
                                        alt="" 
                                        className="w-16 h-12 object-cover rounded"
                                      />
                                    ) : (
                                      <div className={`p-3 rounded-lg ${config.color} text-white`}>
                                        <Icon className="h-5 w-5" />
                                      </div>
                                    )}
                                    
                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium dark:text-white flex items-center gap-2">
                                        {event.title}
                                        {event.design_id && (
                                          <Badge variant="outline" className="text-purple-500 border-purple-500">
                                            <Palette className="h-3 w-3 mr-1" /> Design
                                          </Badge>
                                        )}
                                      </h4>
                                      <p className="text-sm text-muted-foreground dark:text-slate-400">
                                        {new Date(event.start).toLocaleDateString('en-US', { 
                                          weekday: 'short', 
                                          month: 'short', 
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                    
                                    {/* Badges */}
                                    <div className="flex items-center gap-2">
                                      <Badge className={statusCfg.color}>
                                        {statusCfg.label}
                                      </Badge>
                                      {event.channel && (
                                        <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 capitalize">
                                          {event.channel}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground dark:text-slate-400">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No scheduled events for this period</p>
                      {canCreate && (
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setShowEventDialog(true)}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Create Event
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Event Detail Side Panel */}
        {selectedEvent && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/20 z-30"
              onClick={() => setSelectedEvent(null)}
            />
            <EventDetailPanel
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onUpdate={updateEvent}
              onDelete={deleteEvent}
              onOpenDesign={openDesign}
              permissions={permissions}
            />
          </>
        )}

        {/* Create/Edit Event Dialog */}
        <EventDialog
          open={showEventDialog}
          onClose={() => { setShowEventDialog(false); setPendingDesignData(null); }}
          onSubmit={createEvent}
          event={editingEvent}
          designData={pendingDesignData}
        />
      </div>
    </Layout>
  );
};
